import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
  Autocomplete,
  MenuItem,
  Divider,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useOutletContext, useNavigate } from 'react-router-dom';
import config from '../config';
import { useCopilot } from '../contexts/CopilotContext';
import { getCategoryColor } from '../theme';

type UploadStatus = 'in_dashboard' | 'in_accounting' | 'pending' | 'declined' | 'different_file' | 'unverified' | null;

const UPLOAD_STATUS_LABELS: Record<Exclude<UploadStatus, null>, string> = {
  in_dashboard: 'Yes, in dashboard',
  in_accounting: 'Yes, in Accounting',
  pending: 'Awaiting approval',
  declined: 'Declined in Approvals',
  different_file: 'Same name, different file',
  unverified: 'Same name, not verified',
};

interface CertificateProvider {
  id: string;
  provider_email: string;
  provider_name: string;
  certificate_types: string[];
  is_active: boolean;
  last_email_check: string | null;
  notes: string;
  created_at: string;
  updated_at: string;
}

interface Certificate {
  id: string;
  property_address: string;
  certificate_type: string;
  issue_date: string;
  expiry_date: string;
  pdf_filename?: string;
}

interface CertificateRenewalsPageProps {
}

const CertificateRenewalsPage: React.FC<CertificateRenewalsPageProps> = () => {
  const { token } = useAuth();
  const { palette } = useTheme();
  const navigate = useNavigate();
  const { showMessage } = useCopilot();
  const { refreshPendingRenewalsCount, refreshCopilotData } = useOutletContext<{ refreshPendingRenewalsCount: () => void; refreshCopilotData: () => void }>();
  const [providers, setProviders] = useState<CertificateProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProvider, setEditingProvider] = useState<CertificateProvider | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState<CertificateProvider | null>(null);

  // Form state
  const [providerEmail, setProviderEmail] = useState('');
  const [providerName, setProviderName] = useState('');
  const [certificateTypes, setCertificateTypes] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  
  // Email scan states
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState<{
    success: boolean;
    message: string;
    certificates_found?: number;
    providers_scanned?: string[];
    error?: string;
  } | null>(null);
  // Sender check states (read-only list of PDFs from one provider)
  const [checkProviderId, setCheckProviderId] = useState('');
  const [checkDays, setCheckDays] = useState(30);
  const [isChecking, setIsChecking] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [busyPdf, setBusyPdf] = useState<string | null>(null);  // `${message_id}:${filename}` of the PDF being opened/imported
  const [checkNotice, setCheckNotice] = useState<{ severity: 'success' | 'info' | 'error'; text: string } | null>(null);
  const [checkResult, setCheckResult] = useState<{
    provider_name: string;
    days: number;
    truncated: boolean;
    messages: {
      sent_at: string;
      subject: string;
      pdfs: { filename: string; message_id: string; status: UploadStatus; address: string; note?: string }[];
    }[];
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [dismissedTypes, setDismissedTypes] = useState<string[]>([]);
  const [userCertTypes, setUserCertTypes] = useState<string[]>([]);

  const allCertificateTypes = [
    'Gas Certificate',
    'Insurance Certificate',
    'Licence Certificate',
    'Electrical Certificate',
    'Fire Certificate',
    'Emergency Lighting Certificate',
    'Smoke Certificate',
    'Deposit Protection Certificate',
    'Energy Certificate',
    'Other'
  ];

  useEffect(() => {
    fetchProviders();
    fetchUserCertTypes();
  }, []);

  useEffect(() => {
    const handler = () => fetchProviders();
    window.addEventListener('copilot-providers-updated', handler);
    return () => window.removeEventListener('copilot-providers-updated', handler);
  }, []);

  const fetchUserCertTypes = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/api/certificates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) return;
      const data = await response.json();
      const types: string[] = Array.from(new Set(
        (data.certificates || [])
          .map((c: any) => c.certificate_type_category || c.certificate_type)
          .filter(Boolean)
      ));
      setUserCertTypes(types);
    } catch { /* silent */ }
  };

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${config.apiUrl}/api/certificate-providers`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch certificate providers');
      }

      const data = await response.json();
      setProviders(data.providers || []);
    } catch (err: any) {
      showMessage({ text: (err as any).message || 'Failed to load providers.' });
    } finally {
      setLoading(false);
    }
  };

  // Types already covered by at least one provider
  const coveredTypes = new Set(providers.flatMap(p => p.certificate_types));
  // Among cert types the user actually has, show rows for those without a provider yet
  const uncoveredTypes = userCertTypes.filter(
    t => t !== 'Other' && !coveredTypes.has(t) && !dismissedTypes.includes(t)
  );

  const handleOpenDialog = (provider?: CertificateProvider, prefillType?: string) => {
    if (provider) {
      setEditingProvider(provider);
      setProviderEmail(provider.provider_email);
      setProviderName(provider.provider_name);
      setCertificateTypes(provider.certificate_types);
      setIsActive(provider.is_active);
      setNotes(provider.notes || '');
    } else {
      setEditingProvider(null);
      setProviderEmail('');
      setProviderName('');
      setCertificateTypes(prefillType ? [prefillType] : []);
      setIsActive(true);
      setNotes('');
    }
    setFormError(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProvider(null);
    setFormError(null);
  };

  const handleEmailScan = async () => {
    if (!token) {
      setScanResult({
        success: false,
        message: 'Please log in to scan emails',
        error: 'Not authenticated'
      });
      return;
    }

    setIsScanning(true);
    setScanResult(null);

    try {
      const response = await fetch(`${config.apiUrl}/api/gmail/manual-scan`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok) {
        setScanResult({
          success: true,
          message: data.message,
          certificates_found: data.certificates_found,
          providers_scanned: data.providers_scanned
        });
        // Newly found certificates land on the Approvals page — refresh the sidebar badge
        refreshPendingRenewalsCount();
      } else {
        // Handle specific error cases
        if (data.needs_auth) {
          setScanResult({
            success: false,
            message: data.message || 'Gmail not connected. Please set up Gmail in the Integrations tab.',
            error: data.error
          });
        } else {
          setScanResult({
            success: false,
            message: data.message || 'Failed to scan emails',
            error: data.error
          });
        }
      }
    } catch (error) {
      console.error('Error scanning emails:', error);
      setScanResult({
        success: false,
        message: 'Network error while scanning emails',
        error: String(error)
      });
    } finally {
      setIsScanning(false);
    }
  };

  const handleCheckSender = async (keepNotice = false) => {
    setIsChecking(true);
    setCheckError(null);
    if (!keepNotice) {
      setCheckNotice(null);
      setCheckResult(null);  // a refresh after an import keeps the table in view
    }

    try {
      const response = await fetch(
        `${config.apiUrl}/api/gmail/provider-pdfs?provider_id=${encodeURIComponent(checkProviderId)}&days=${checkDays}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      if (response.ok) {
        setCheckResult(data);
      } else {
        setCheckError(data.needs_auth ? 'Gmail not connected. Please set up Gmail in the Integrations tab.' : (data.error || 'Failed to check emails'));
      }
    } catch (error) {
      console.error('Error checking sender emails:', error);
      setCheckError('Network error while checking emails');
    } finally {
      setIsChecking(false);
    }
  };

  const pdfParams = (pdf: { filename: string; message_id: string }) =>
    `provider_id=${encodeURIComponent(checkProviderId)}&message_id=${encodeURIComponent(pdf.message_id)}&filename=${encodeURIComponent(pdf.filename)}`;

  const handleInspectPdf = async (pdf: { filename: string; message_id: string }) => {
    setBusyPdf(`${pdf.message_id}:${pdf.filename}`);
    setCheckNotice(null);
    // Open the tab straight away (inside the click) so the browser doesn't block it, then point it at the PDF
    const tab = window.open('', '_blank');
    try {
      // A new tab can't send the login header, so fetch the PDF here and show it from a local blob URL
      const response = await fetch(`${config.apiUrl}/api/gmail/provider-pdfs/file?${pdfParams(pdf)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Could not open the PDF');
      }
      const url = URL.createObjectURL(await response.blob());
      if (tab) {
        tab.location.href = url;
      }
    } catch (error) {
      tab?.close();
      setCheckNotice({ severity: 'error', text: (error as Error).message || 'Could not open the PDF' });
    } finally {
      setBusyPdf(null);
    }
  };

  const handleUploadPdf = async (pdf: { filename: string; message_id: string }) => {
    setBusyPdf(`${pdf.message_id}:${pdf.filename}`);
    setCheckNotice(null);
    try {
      const response = await fetch(`${config.apiUrl}/api/gmail/provider-pdfs/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider_id: checkProviderId, message_id: pdf.message_id, filename: pdf.filename })
      });
      const data = await response.json();
      if (!response.ok) {
        setCheckNotice({ severity: 'error', text: `${pdf.filename}: ${data.error || 'Import failed'}` });
        return;
      }
      setCheckNotice(data.duplicate
        ? { severity: 'info', text: `${pdf.filename} was already imported (${data.message || 'duplicate'}).` }
        : { severity: 'success', text: `${pdf.filename} sent to ${data.destination === 'accounting' ? 'Accounting' : 'Approvals'} for review.` });
      refreshPendingRenewalsCount();
      await handleCheckSender(true);  // refresh the table so the status reflects the import
    } catch (error) {
      console.error('Error importing PDF:', error);
      setCheckNotice({ severity: 'error', text: 'Network error while importing the PDF' });
    } finally {
      setBusyPdf(null);
    }
  };

  const handleSaveProvider = async () => {
    // Validation
    if (!providerEmail || !providerName || certificateTypes.length === 0) {
      setFormError('Please fill in all required fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(providerEmail)) {
      setFormError('Please enter a valid email address');
      return;
    }

    try {
      setSaving(true);
      setFormError(null);

      const url = editingProvider
        ? `${config.apiUrl}/api/certificate-providers/${editingProvider.id}`
        : `${config.apiUrl}/api/certificate-providers`;

      const method = editingProvider ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          provider_email: providerEmail,
          provider_name: providerName,
          certificate_types: certificateTypes,
          is_active: isActive,
          notes: notes
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 409 && errorData.existing_provider) {
          const existing = errorData.existing_provider;
          const existingTypes: string[] = Array.isArray(existing.certificate_types)
            ? existing.certificate_types
            : (typeof existing.certificate_types === 'string' ? JSON.parse(existing.certificate_types) : []);
          const typeLabel = existingTypes.length > 0
            ? existingTypes.map((t: string) => t.replace(' Certificate', '')).join(' and ')
            : 'another certificate type';
          handleCloseDialog();
          const existingInList = providers.find((pr: any) => pr.provider_email === providerEmail);
          showMessage({
            text: <>You're trying to link the same email again — I can see <strong>{providerEmail}</strong> is already used for <strong>{typeLabel}</strong>. Edit that provider to add another certificate type instead.</>,
            action: {
              label: 'Edit existing provider',
              onClick: () => {
                if (existingInList) {
                  setEditingProvider(existingInList);
                  setCertificateTypes(existingTypes);
                  setProviderName(existingInList.provider_name || '');
                  setProviderEmail(existingInList.provider_email);
                  setIsActive(existingInList.is_active ?? true);
                  setNotes(existingInList.notes || '');
                  setOpenDialog(true);
                }
              },
            },
            laterLabel: 'Got it',
          });
          return;
        }
        throw new Error(errorData.error || 'Failed to save provider');
      }

      await fetchProviders();
      handleCloseDialog();
      refreshCopilotData();
    } catch (err: any) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProvider = async () => {
    if (!providerToDelete) return;

    try {
      const response = await fetch(
        `${config.apiUrl}/api/certificate-providers/${providerToDelete.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete provider');
      }

      await fetchProviders();
      setDeleteConfirmOpen(false);
      setProviderToDelete(null);
    } catch (err: any) {
      showMessage({ text: (err as any).message || 'Failed to delete provider.' });
    }
  };

  const handleToggleActive = async (provider: CertificateProvider) => {
    try {
      const response = await fetch(
        `${config.apiUrl}/api/certificate-providers/${provider.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...provider,
            is_active: !provider.is_active
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update provider status');
      }

      await fetchProviders();
    } catch (err: any) {
      showMessage({ text: (err as any).message || 'Failed to update provider.' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, pt: 5 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' }, alignItems: 'center', gap: 3, mb: 3 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4" component="h1" sx={{ mt: 0.5, mb: 1, fontWeight: 700, letterSpacing: '-0.025em' }}>Providers</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            Keep your engineers and certificate providers together, so Mill knows who to contact and which documents to look out for.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', flexShrink: 0 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Provider
          </Button>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Card variant="outlined" sx={{ borderColor: palette.tokens.hairline }}>
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom>Add a provider and their certificate types</Typography>
            <Typography variant="body2" color="text.secondary">
              Enter their name and the exact email address you communicate with them through, and tell us what certificate types they handle, such as Gas or Electrical. This way, we know who handles which renewals.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              One provider can cover several types.
            </Typography>
          </CardContent>
        </Card>
      </Box>


      {/* Providers Section */}
      <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2 }}>
        Your providers
      </Typography>

      <Card elevation={0} sx={{ border: `1px solid ${palette.tokens.hairline}` }}>
        <CardContent>
          {providers.length === 0 && uncoveredTypes.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <EmailIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No Certificate Providers
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Add your first provider, choose the certificate types they handle, then connect Gmail to scan for PDF documents.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Add Your First Provider
              </Button>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Provider Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Certificate Types</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Check</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {providers.map((provider) => (
                    <TableRow key={provider.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {provider.provider_name}
                        </Typography>
                        {provider.notes && (
                          <Typography variant="caption" color="textSecondary">
                            {provider.notes}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{provider.provider_email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {provider.certificate_types.map((type, idx) => (
                            <Chip
                              key={idx}
                              label={type}
                              size="small"
                              sx={{
                                backgroundColor: getCategoryColor(type),
                                color: palette.tokens.white,
                                fontWeight: 500,
                                fontSize: '0.7rem',
                              }}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={provider.is_active ? 'Click to disable' : 'Click to enable'}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleActive(provider)}
                          >
                            {provider.is_active ? (
                              <ActiveIcon color="success" />
                            ) : (
                              <InactiveIcon color="disabled" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {provider.last_email_check
                            ? new Date(provider.last_email_check).toLocaleDateString()
                            : 'Never'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(provider)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setProviderToDelete(provider);
                            setDeleteConfirmOpen(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Placeholder rows — one per cert type the user has but hasn't assigned a provider to */}
                  {uncoveredTypes.map((type) => (
                    <TableRow
                      key={`placeholder-${type}`}
                      onClick={() => handleOpenDialog(undefined, type)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: '#fafbff',
                        '&:hover': { backgroundColor: '#f0f4ff' },
                        borderLeft: `3px solid ${getCategoryColor(type)}`,
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ color: palette.tokens.mutedSoft, fontStyle: 'italic' }}>
                          Add provider name…
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: palette.tokens.mutedSoft, fontStyle: 'italic' }}>
                          Add email…
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={type}
                          size="small"
                          sx={{
                            backgroundColor: getCategoryColor(type),
                            color: palette.tokens.white,
                            fontWeight: 500,
                            fontSize: '0.7rem',
                          }}
                        />
                      </TableCell>
                      <TableCell colSpan={2} />
                      <TableCell align="right" onClick={e => e.stopPropagation()}>
                        <Tooltip title="Dismiss">
                          <IconButton
                            size="small"
                            onClick={() => setDismissedTypes(prev => [...prev, type])}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Card component="section" aria-label="Inbox scan safety check" variant="outlined" sx={{ mt: 3, borderColor: palette.tokens.hairline }}>
        <CardContent>
          <Typography variant="overline" color="text.secondary">Missing a certificate?</Typography>
          <Typography variant="h6" component="h2" gutterBottom>Scan your email inbox</Typography>
          <Typography variant="body2" color="text.secondary">
            Click Scan Email Inbox to check the latest emails from your registered providers. A certificate may have been missed if your Gmail connection expired, for example. Reconnect Gmail if needed, then run a scan to catch up.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            We check up to 100 matching emails from the earliest saved provider check date, or the last 30 days if any provider has no previous check.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Certificates go to Approvals for review; invoices go to Accounting. Emails without PDFs, including inline logos and PNG/JPEG attachments, are not imported by this scan.
          </Typography>
          <Tooltip title="Check PDF attachments from registered providers in your connected Gmail account. Requires Gmail to be connected.">
            <span>
              <Button sx={{ mt: 2 }} variant="outlined"
                startIcon={isScanning ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                onClick={handleEmailScan} disabled={isScanning || providers.length === 0}>
                {isScanning ? 'Scanning...' : 'Scan Email Inbox'}
              </Button>
            </span>
          </Tooltip>
          {scanResult && (
            <Alert severity={scanResult.success ? 'success' : 'error'} sx={{ mt: 2 }} onClose={() => setScanResult(null)}>
              {scanResult.message}
              {!!scanResult.providers_scanned?.length && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                  Scanned: {scanResult.providers_scanned.join(', ')}
                </Typography>
              )}
            </Alert>
          )}

          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" component="h3" gutterBottom>Check one sender</Typography>
          <Typography variant="body2" color="text.secondary">
            Pick a registered provider to list every email with PDF attachments they sent in the chosen period: the PDF names, when each email was sent, its subject, and whether the same file is already in your dashboard, with its address. Names are compared first, and only files with a matching name are downloaded and compared by content. This only looks, so nothing is imported.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mt: 2 }}>
            <TextField select size="small" label="Sender" value={checkProviderId}
              onChange={(e) => setCheckProviderId(e.target.value)} sx={{ minWidth: 260 }}>
              {providers.map(p => (
                <MenuItem key={p.id} value={p.id}>{p.provider_name} ({p.provider_email})</MenuItem>
              ))}
            </TextField>
            <TextField size="small" type="number" label="Days back (max 60)" value={checkDays}
              onChange={(e) => setCheckDays(Math.min(60, Math.max(1, Number(e.target.value) || 1)))}
              slotProps={{ htmlInput: { min: 1, max: 60 } }} sx={{ width: 170 }} />
            <Button variant="outlined"
              startIcon={isChecking ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              onClick={() => handleCheckSender()} disabled={isChecking || !checkProviderId}>
              {isChecking ? 'Checking...' : 'Check emails'}
            </Button>
          </Box>
          {checkNotice && (
            <Alert severity={checkNotice.severity} sx={{ mt: 2 }} onClose={() => setCheckNotice(null)}>{checkNotice.text}</Alert>
          )}
          {checkError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setCheckError(null)}>{checkError}</Alert>
          )}
          {checkResult && (
            <Box sx={{ mt: 2 }}>
              {checkResult.messages.length === 0 ? (
                <Alert severity="info">No emails with PDF attachments from {checkResult.provider_name} in the last {checkResult.days} days.</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small" aria-label="PDFs found from sender">
                    <TableHead>
                      <TableRow>
                        <TableCell>Sent</TableCell>
                        <TableCell>Email subject</TableCell>
                        <TableCell>PDF file</TableCell>
                        <TableCell>Already uploaded?</TableCell>
                        <TableCell>Address</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {checkResult.messages.map((m, i) => m.pdfs.map((pdf, j) => (
                        <TableRow key={`${i}-${j}`}>
                          {j === 0 && (
                            <>
                              <TableCell rowSpan={m.pdfs.length} sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>{new Date(m.sent_at).toLocaleString()}</TableCell>
                              <TableCell rowSpan={m.pdfs.length} sx={{ verticalAlign: 'top' }}>{m.subject}</TableCell>
                            </>
                          )}
                          <TableCell>{pdf.filename}</TableCell>
                          <TableCell>
                            {pdf.status ? UPLOAD_STATUS_LABELS[pdf.status] : 'No'}
                            {pdf.note && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{pdf.note}</Typography>}
                          </TableCell>
                          <TableCell>{pdf.address}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {(pdf.status === null || pdf.status === 'different_file') && (
                              <>
                                <Button size="small" disabled={busyPdf !== null} onClick={() => handleInspectPdf(pdf)}>Inspect</Button>
                                <Button size="small" variant="outlined" sx={{ ml: 1 }} disabled={busyPdf !== null}
                                  startIcon={busyPdf === `${pdf.message_id}:${pdf.filename}` ? <CircularProgress size={14} color="inherit" /> : undefined}
                                  onClick={() => handleUploadPdf(pdf)}>Upload</Button>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      )))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {checkResult.truncated && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Showing the newest 100 matching emails. Choose fewer days to see the rest.
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Provider Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingProvider ? 'Edit Provider' : 'Add Certificate Provider'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Add a contact, not a document. Tell Mill which email address you use to communicate with this provider and which certificate types they handle.
          </Typography>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Provider Name"
            value={providerName}
            onChange={(e) => setProviderName(e.target.value)}
            margin="normal"
            required
            placeholder="e.g., James Fire Safety Ltd"
          />

          <TextField
            fullWidth
            label="Provider Email"
            type="email"
            value={providerEmail}
            onChange={(e) => setProviderEmail(e.target.value)}
            margin="normal"
            required
            placeholder="e.g., JamesFire@gmail.com"
            helperText="Use the exact email address you communicate with this provider through."
          />

          <Autocomplete
            multiple
            options={allCertificateTypes}
            value={certificateTypes}
            onChange={(_, newValue) => setCertificateTypes(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Certificate Types"
                margin="normal"
                required
                placeholder="Select certificate types"
                helperText="Choose what this provider handles. You can select several types; no certificate is uploaded here."
              />
            )}
            sx={{ mt: 2 }}
          />

          <TextField
            fullWidth
            label="Notes (Optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            margin="normal"
            multiline
            rows={2}
            placeholder="e.g., Contact details, special instructions"
          />

        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveProvider}
            variant="contained"
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : editingProvider ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Provider?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{providerToDelete?.provider_name}</strong>?
            This will stop automatic monitoring for certificates from this provider.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteProvider} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CertificateRenewalsPage;
